import type { CreateVoucherRequest } from '../types';

// Draft management constants
export const DRAFT_STORAGE_KEY = 'voucher_create_draft';
export const DRAFT_EXPIRATION_TIME = 60 * 60 * 1000; // 1 hour in milliseconds

export interface DraftData {
  formData: CreateVoucherRequest;
  timestamp: number;
  currentStep: number;
}

/**
 * Save draft data to session storage with timestamp
 */
export const saveDraftToSessionStorage = (
  formData: CreateVoucherRequest,
  currentStep: number
): void => {
  try {
    const draftData: DraftData = {
      formData,
      timestamp: Date.now(),
      currentStep,
    };
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
  } catch (error) {
    console.warn('Failed to save draft to session storage:', error);
  }
};

/**
 * Load draft data from session storage, checking for expiration
 */
export const loadDraftFromSessionStorage = (): DraftData | null => {
  try {
    const storedData = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!storedData) return null;

    const draftData: DraftData = JSON.parse(storedData);
    const isExpired = Date.now() - draftData.timestamp > DRAFT_EXPIRATION_TIME;

    if (isExpired) {
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      return null;
    }

    return draftData;
  } catch (error) {
    console.warn('Failed to load draft from session storage:', error);
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    return null;
  }
};

/**
 * Clear draft data from session storage
 */
export const clearDraftFromSessionStorage = (): void => {
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear draft from session storage:', error);
  }
};

/**
 * Check if form data has changes compared to default values
 */
export const hasFormChanges = (
  formData: Record<string, unknown>,
  defaultValues: CreateVoucherRequest
): boolean => {
  return Object.keys(formData).some((key) => {
    const fieldKey = key as keyof CreateVoucherRequest;
    const currentValue = formData[fieldKey];
    const defaultValue = defaultValues[fieldKey];

    // Handle array comparison
    if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
      return JSON.stringify(currentValue) !== JSON.stringify(defaultValue);
    }

    // Handle object comparison
    if (typeof currentValue === 'object' && typeof defaultValue === 'object') {
      return JSON.stringify(currentValue) !== JSON.stringify(defaultValue);
    }

    return currentValue !== defaultValue;
  });
};
